import type { EventHint } from '@sentry/vue';
import { browserTracingIntegration, init } from '@sentry/vue';
import type { App } from 'vue';
import type { Router } from 'vue-router';
import { AbortError, ForbiddenError, UnauthorizedError, ValidationError } from "@/api/lib/errors";
import * as Sentry from "@sentry/browser";
import {
  appEnv,
  isProductionEnv,
  platform,
  getVersionString,
  getAppInfos,
  commit
} from "@/application/utils/envInfo";

const isIgnoredImageResourceError = (error: EventHint['originalException']): boolean => {
  if (!(error instanceof Event)) {
    return false;
  }

  const target = error.target;

  if (!(target instanceof HTMLImageElement)) {
    return false;
  }

  const src = target.currentSrc || target.src;

  return /\/api\/gesellschaft\/\d+\/logo(\?|$)/.test(src);
};

// Generic browser network-failure messages (Chrome: "Failed to fetch", Safari/WebKit: "Load failed").
// Matched on message rather than `instanceof TypeError` because pdf.js re-wraps the underlying fetch
// TypeError as its own `UnknownErrorException` (extends Error, not TypeError) while keeping the message.
const isNetworkFailureMessage = (message: string): boolean =>
  message.startsWith('Failed to fetch') || message.startsWith('Load failed');

const isErrorToIgnore = (error: EventHint['originalException']): boolean => [
  error instanceof UnauthorizedError,
  error instanceof AbortError,
  error instanceof ValidationError,
  error instanceof ForbiddenError,
  error instanceof Error && isNetworkFailureMessage(error.message),
  isIgnoredImageResourceError(error),
].some(Boolean);

export const initSentry = (app: App, router: Router | undefined): void => {
  // use sentry only for production to reduce the cost for SaaS Sentry
  if (!isProductionEnv) return;

  init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: appEnv,
    integrations: [
      browserTracingIntegration({ router }),
      Sentry.rewriteFramesIntegration(),
    ],
    tracesSampler: ({ inheritOrSampleWith }) => {
      return inheritOrSampleWith(0.1);
    },
    denyUrls: [
      'https://cloud.ccm19.de',
      /webkit-masked-url:\/\/hidden/i,
      /googleapis\.com/i,
      /regprotect-web-component/i,
      /ba-regprotect/i,
      /banksapi\/embeddable-finance-web-components/i,
    ],
    beforeSend: (event, { originalException }) => isErrorToIgnore(originalException) ? null : event,
  });

  Sentry.setTag('commit', commit);
  Sentry.setTag('platform', platform);
  getAppInfos().then((info) => {
    Sentry.setTag('build', info.build);
    Sentry.setTag('version', info.version);
  });
  getVersionString().then((versionString) => {
    Sentry.setTag('versionString', versionString);
  });
};
